<template>
	<AdministrationUsersLayout user-type="Admin Portal">
		<template #content>
			<FilterBar
				:search-query.sync="adminPortalUserSearchQuery"
				search-query-label="Name"
				:selected-brand-id.sync="selectedAdminPortalBrandId"
				@persistSearchQuery="changeAdminPortalUserSearchQuery"
				@persistSelectedBrandId="changeAdminPortalBrandFilter"
				@clearFilters="clearFilters"
			>
				<template #after-filters>
					<Dropdown
						v-model="selectedAdminPortalUserStatus"
						label="Status"
						:items="statusDropdownItems"
						custom-sort
						style="flex: none;"
						@input="changeAdminPortalUserStatusFilter($event)"
					/>
				</template>
				<template #action>
					<PrimaryActionButton
						v-if="userCanCreateUpdate"
						@click="onCreateClick"
					>
						<v-icon
							left
							dark
						>
							mdi-plus
						</v-icon>
						Create
					</PrimaryActionButton>
				</template>
			</FilterBar>
			<SectionCard>
				<template #title>
					Admin Portal Users
				</template>
				<template #body>
					<DataTable
						:items="filteredUsers"
						:headers="tableHeaders"
						sort-by="userId"
						sort-desc
						@click:row="onEditClick"
					>
						<template #item.brandNames="{ item }">
							<div class="d-flex align-center">
								<span>
									{{ item.brandName }}
								</span>
								<v-tooltip
									v-if="item.additionalBrandsCount > 0"
									bottom
								>
									<template #activator="{ on, attrs }">
										<Chip
											v-bind="attrs"
											class="ml-2 prussian-blue white--text rounded-pill"
											v-on="on"
										>
											+{{ item.additionalBrandsCount }} additional...
										</Chip>
									</template>
									<span>
										{{ item.allBrandNames }}
									</span>
								</v-tooltip>
							</div>
						</template>
						<template #item.name="{ item }">
							<span>
								{{ item.name }}
							</span>
							<v-tooltip
								v-if="adminPortalScimEnabled && item.scimExternalId && item.scimExternalId.trim() !== ''"
								bottom
							>
								<template #activator="{ on, attrs }">
									<span
										v-bind="attrs"
										v-on="on"
									>
										<Chip
											class="ml-2"
											color="primary"
										>
											SCIM
										</Chip>
									</span>
								</template>
								Externally managed via SCIM (ID: {{ item.scimExternalId }})
							</v-tooltip>
						</template>
						<template #item.isActive="{ item }">
							{{ item.isActive ? 'Active' : 'Inactive' }}
						</template>
						<template #item.action="{ item }">
							<v-menu
								offset-y
								left
							>
								<template #activator="{ on, attrs }">
									<v-btn
										icon
										v-bind="attrs"
										v-on="on"
										@click.stop
									>
										<v-icon>
											mdi-dots-horizontal
										</v-icon>
									</v-btn>
								</template>
								<v-list dense>
									<v-list-item
										v-if="userCanCreateUpdate && userHasAllBrands(item.brandIds)"
										@click="onEditClick(item)"
									>
										<v-list-item-title>
											Edit
										</v-list-item-title>
									</v-list-item>
									<v-list-item
										v-else
										@click="onEditClick(item)"
									>
										<v-list-item-title>
											View
										</v-list-item-title>
									</v-list-item>
									<v-list-item
										v-if="userCanCreateUpdate && userHasAllBrands(item.brandIds)"
										@click="onRemoveClick(item)"
									>
										<v-list-item-title>
											Delete
										</v-list-item-title>
									</v-list-item>
								</v-list>
							</v-menu>
						</template>
					</DataTable>
				</template>
			</SectionCard>
			<ConfirmDeleteModal
				v-if="userToBeRemoved"
				:entity-name="userToBeRemoved.name"
				entity-type="Admin Portal User"
				@close="userToBeRemoved = null"
				@delete="deleteUser"
			/>
		</template>
	</AdministrationUsersLayout>
</template>
<script>
import { mapGetters } from 'vuex'
import AdministrationUsersLayout from '../administration-users-layout.vue'
import Dropdown from '../../../../../../shared/components/dropdown.vue'
import PrimaryActionButton from '../../../../../../shared/components/primary-action-button.vue'
import DataTable from '../../../../../../shared/components/data-table.vue'
import SectionCard from '../../../../../../shared/components/section-card.vue'
import { showSnackbar } from '../../../../../../shared/state/snackbar.js'
import {
	adminPortalUsers,
	adminPortalProductRoles,
	loadAdminPortalUsers,
	legacyPermissionGroups,
	changeAdminPortalUserSearchQuery,
	changeAdminPortalUserStatusFilter,
	adminPortalUserSearchQuery,
	selectedAdminPortalUserStatus,
	adminPortalScimEnabled
} from '../../../../../../shared/state/admin-portal-users-and-lookup-data.js'
import { changeAdminPortalBrandFilter, selectedAdminPortalBrandId, userHasAllBrands, brandOptions } from '../../../../../../shared/state/brands.js'
import { useLabels } from '../../../../../../shared/state/admin-portal-navigation.js'
import { ADMIN_PORTAL_MANAGE_USER } from '../../../../router/route-names.js'
import FilterBar from '../../../../../../shared/components/filter-bar.vue'
import ConfirmDeleteModal from '../../../../../../shared/components/confirm-delete-modal.vue'
import Chip from '../../../../../../shared/components/chip.vue'
import { deleteAdminPortalUser } from '../../../../../../shared/utils/api/admin-portal-users.js'
import { USERS_MODULE_FULL_PERMISSIONS, ADMIN_PORTAL_USERS_MODULE_FULL_PERMISSIONS } from '../../../../../../shared/permissions/admin-portal-permissions.js'

export default {
	components: {
		ConfirmDeleteModal,
		FilterBar,
		AdministrationUsersLayout,
		Dropdown,
		PrimaryActionButton,
		DataTable,
		SectionCard,
		Chip
	},
	setup () {
		return {
			userHasAllBrands,
			showSnackbar,
			adminPortalUsers,
			adminPortalProductRoles,
			changeAdminPortalUserSearchQuery,
			changeAdminPortalUserStatusFilter,
			adminPortalUserSearchQuery,
			selectedAdminPortalUserStatus,
			changeAdminPortalBrandFilter,
			selectedAdminPortalBrandId,
			adminPortalScimEnabled,
			brandOptions,
			useLabels
		}
	},
	data () {
		return {
			userToBeRemoved: null,
			// Brand display constants
			NO_BRAND_TEXT: 'No brand assigned',
			UNKNOWN_BRAND_TEXT: 'Unknown Brand'
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		tableHeaders () {
			return [
				{
					value: 'userId',
					text: 'ID',
					width: '5%'
				},
				{
					value: 'brandNames',
					text: useLabels('Brands'),
					width: '20%'
				},
				{
					value: 'name',
					text: 'Name',
					width: '25%'
				},
				{
					value: 'emailAddress',
					text: 'Email Address',
					width: '30%'
				},
				{
					value: 'isActive',
					text: 'Status',
					width: '10%'
				},
				{
					value: 'createdDate',
					text: 'Created Date',
					width: '10%'
				},
				{
					value: 'action',
					text: 'Action',
					width: '80px',
					align: 'center',
					sortable: false
				}
			]
		},
		filteredUsers () {
			// Create a map for O(1) brand lookups
			const brandMap = new Map(brandOptions.value.map(b => [b.value, b.text]))

			return this.adminPortalUsers.filter(({ name, isActive, brandIds }) => {
				let check = true
				if (adminPortalUserSearchQuery.value) check = name.toLowerCase().includes(adminPortalUserSearchQuery.value.toLowerCase())
				if (selectedAdminPortalBrandId.value !== null) check = check && brandIds.includes(selectedAdminPortalBrandId.value)
				if (selectedAdminPortalUserStatus.value !== null) check = check && Boolean(isActive) === selectedAdminPortalUserStatus.value
				return check
			}).map(user => {
				// Map brandIds to brand names
				const brandIds = user.brandIds || []
				const brandNames = brandIds.map(brandId =>
					brandMap.get(brandId) || this.UNKNOWN_BRAND_TEXT
				)

				// First brand name to display, with fallback
				const brandName = brandNames.length > 0 ? brandNames[0] : this.NO_BRAND_TEXT
				// Count of additional brands (0 when single/no brand, positive when multiple brands)
				const additionalBrandsCount = Math.max(0, brandNames.length - 1)
				// All brand names for tooltip (keep consistent with fallback text)
				const allBrandNames = brandNames.length > 0
					? brandNames.join(', ')
					: this.NO_BRAND_TEXT

				return {
					...user,
					brandName,
					additionalBrandsCount,
					allBrandNames,
					legacyPermissionGroups: user.legacyPermissionGroupIds?.map(userLegacyPermissionGroup => {
						const matchingPermissionGroup = legacyPermissionGroups.value.find(({ permissionGroupId }) => permissionGroupId === userLegacyPermissionGroup)
						return matchingPermissionGroup ? matchingPermissionGroup.permissionGroupName : ''
					})
				}
			})
		},
		statusDropdownItems () {
			return [
				{
					text: 'View All',
					value: null
				},
				{
					text: 'Active',
					value: true
				},
				{
					text: 'Inactive',
					value: false
				}
			]
		},
		userCanCreateUpdate () {
			if (this.productAreaPermission(USERS_MODULE_FULL_PERMISSIONS) === true) {
				return true
			}
			return this.productAreaPermission(ADMIN_PORTAL_USERS_MODULE_FULL_PERMISSIONS)
		}
	},
	async created () {
		await loadAdminPortalUsers()
	},
	methods: {
		onCreateClick () {
			this.$router.push({
				name: ADMIN_PORTAL_MANAGE_USER
			})
		},
		onEditClick (userToEdit) {
			this.$router.push({
				name: ADMIN_PORTAL_MANAGE_USER,
				params: {
					userToEdit
				}
			})
		},
		async onRemoveClick (userToBeRemoved) {
			this.userToBeRemoved = userToBeRemoved
		},
		async deleteUser () {
			await deleteAdminPortalUser(this.userToBeRemoved.userId)
			await loadAdminPortalUsers()
			this.userToBeRemoved = null
			this.showSnackbar('This user has been deleted')
		},
		clearFilters () {
			changeAdminPortalUserStatusFilter(null)
		}
	}
}
</script>
